import { motion } from 'framer-motion';
import { MessageSquare, Phone, AtSign, MailQuestion, Mail, Footprints } from 'lucide-react';
import type { CustomerRecord, Channel } from '@/domain/types';
import { customerName, relativeFromIso } from '@/utils/format';
import clsx from 'clsx';

const channelIcon: Record<Channel, typeof MessageSquare> = {
  'meta-dm': MessageSquare,
  'ig-dm': AtSign,
  sms: MessageSquare,
  voice: Phone,
  'web-form': MailQuestion,
  email: Mail,
  'walk-in': Footprints,
};

export interface QueueItemProps {
  customer: CustomerRecord;
  channel: Channel;
  intent: string;
  recommendedAction: string;
  lastEventIso: string;
  studioContext?: string;
  onClick?: () => void;
}

export default function QueueItem({
  customer,
  channel,
  intent,
  recommendedAction,
  lastEventIso,
  studioContext,
  onClick,
}: QueueItemProps) {
  const Icon = channelIcon[channel];
  const highValue = customer.leadScore >= 80;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.15 }}
      data-testid={`queue-item-${customer.id}`}
      className={clsx(
        'w-full text-left bg-ink-deep rounded-xl px-5 py-4 border transition-shadow',
        highValue
          ? 'border-gold-bright/70 shadow-gold-rim hover:shadow-gold-glow'
          : 'border-gold-dim/20 hover:border-gold-bright/40',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={14} className="text-gold-bright" />
            <h3 className="font-inter text-base text-cream truncate" data-testid={`queue-item-name-${customer.id}`}>
              {customerName(customer)}
            </h3>
            {highValue ? <span className="pill-gold-solid">High value</span> : null}
            {studioContext ? <span className="pill-cream">{studioContext}</span> : null}
          </div>
          <p className="font-inter text-sm text-cream-soft mt-1 line-clamp-2">{intent}</p>
          <p className="font-inter text-[11px] text-gold-dim uppercase mt-2 tracking-widest">
            {relativeFromIso(lastEventIso)} · {customer.suburb}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className="font-mono text-[11px] text-cream-soft">Score {customer.leadScore}</span>
          <span className="pill-gold text-[10px]">{recommendedAction}</span>
        </div>
      </div>
    </motion.button>
  );
}
